import classnames from 'classnames';
import type { FC } from 'react';

import { Button } from '@/core/tamagoshi/Button/Button';
import { Slider } from '@/core/tamagoshi/Carousel/components/Slider/Slider';
import { Chip } from '@/core/tamagoshi/Chip/Chip';
import { ArrowLeft } from '@/core/tamagoshi/icons/ArrowLeft';
import { ArrowRight } from '@/core/tamagoshi/icons/ArrowRight';
import { Text } from '@/domains/core/tamagoshi/Text/Text';

import { usePlaylistFilter } from './usePlaylistFilter';

import styles from './PlaylistFilter.module.scss';

interface ItemInfo {
  itemName: string;
  itemId: number;
}
interface PlaylistFilterProps {
  items: ItemInfo[];
  selectItem: (brandName: string | null) => void;
  selectedItem: null | string;
  allItemsLabel: string;
  className?: string;
}

export const PlaylistFilter: FC<PlaylistFilterProps> = ({
  items,
  selectItem,
  selectedItem,
  className,
  allItemsLabel,
}) => {
  const { onScrollChange, ref, visibilityState, onControlClick } =
    usePlaylistFilter();

  if (items.length === 0) {
    return null;
  }

  return (
    <nav
      className={classnames(styles.navbar, className)}
      data-has-left-arrow={visibilityState.leftVisible.toString()}
      data-has-right-arrow={visibilityState.rightVisible.toString()}
    >
      <Button
        icon={<ArrowLeft />}
        className={classnames(styles.arrow, styles.arrowLeft)}
        aria-hidden={
          (!visibilityState.leftVisible).toString() as 'true' | 'false'
        }
        onClick={() => onControlClick('left')}
        shape="round"
      />
      <Button
        icon={<ArrowRight />}
        className={classnames(styles.arrow, styles.arrowRight)}
        aria-hidden={
          (!visibilityState.rightVisible).toString() as 'true' | 'false'
        }
        onClick={() => onControlClick('right')}
        shape="round"
      />
      <Slider ref={ref} onScrollChange={onScrollChange}>
        <Chip
          theme={selectedItem === null ? 'default' : 'outline'}
          onClick={() => {
            selectItem(null);
            ref.current?.scrollToIndex(0, false);
          }}
          selected={selectedItem === null}
        >
          <Text type="body2" weight="semiBold" color="inherit">
            {allItemsLabel}
          </Text>
        </Chip>
        {items.map((item) => (
          <Chip
            theme={item.itemName === selectedItem ? 'default' : 'outline'}
            key={item.itemId}
            onClick={() => {
              selectItem(item.itemName);
              ref.current?.scrollToIndex(0, false);
            }}
            selected={item.itemName === selectedItem}
          >
            <Text type="body2" weight="semiBold" color="inherit">
              {item.itemName}
            </Text>
          </Chip>
        ))}
      </Slider>
    </nav>
  );
};

PlaylistFilter.displayName = 'PlaylistFilter';
